/**
 * Tippy.js Utilities
 * Shared utilities for tippy menus across the application
 */
import { Logger } from './logger';
import { formatMenuShortcut, keyboardShortcutHtml, MENU_SHIFT_SYMBOL } from './keyboard-shortcut-display';
const console = new Logger('TippyUtils');
export { formatMenuShortcut, keyboardShortcutHtml, MENU_SHIFT_SYMBOL };
const visibleTippyStack = [];
const tippyEscapeHandlers = new WeakMap();
const nativeContextMenuSuppressedElements = new WeakSet();
let escapePriorityListenerInstalled = false;
function suppressNativeContextMenu(element) {
    if (!element || nativeContextMenuSuppressedElements.has(element)) {
        return;
    }
    nativeContextMenuSuppressedElements.add(element);
    element.dataset.hasContextMenu = 'true';
    element.addEventListener('contextmenu', (event) => {
        event.preventDefault();
    }, true);
    element.addEventListener('pointerdown', (event) => {
        if (event.button !== 2) {
            return;
        }
        event.preventDefault();
    }, true);
}
function removeFromVisibleStack(instance) {
    const idx = visibleTippyStack.lastIndexOf(instance);
    if (idx >= 0) {
        visibleTippyStack.splice(idx, 1);
    }
}
/**
 * Whether any tippy menu registered via addTippyBackdropSupport is visible.
 * Used by modal Escape handling so tippy menus always win.
 */
export function hasVisibleTippyMenus() {
    for (let i = visibleTippyStack.length - 1; i >= 0; i--) {
        const instance = visibleTippyStack[i];
        if (instance?.state?.isVisible) {
            return true;
        }
    }
    return false;
}
function installEscapePriorityListener() {
    if (escapePriorityListenerInstalled) {
        return;
    }
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') {
            return;
        }
        if (visibleTippyStack.length === 0) {
            return;
        }
        const topInstance = visibleTippyStack[visibleTippyStack.length - 1];
        if (!topInstance || !topInstance.state?.isVisible) {
            removeFromVisibleStack(topInstance);
            return;
        }
        // Ensure open tippy menus consume Escape before any app-level handlers.
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        topInstance.hide();
        const onEscape = tippyEscapeHandlers.get(topInstance);
        if (onEscape) {
            onEscape();
        }
    }, true);
    escapePriorityListenerInstalled = true;
}
/**
 * Create or get a backdrop element for modal-like menu behavior
 */
export function getOrCreateBackdrop(className) {
    let backdrop = document.querySelector(`.${className}`);
    if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.className = `plugin-menu-backdrop ${className}`;
        document.body.appendChild(backdrop);
    }
    return backdrop;
}
/**
 * Get current theme for tippy menus
 */
export function getTheme() {
    const root = document.documentElement;
    const theme = root.getAttribute('data-theme');
    return theme === 'light' ? 'light' : 'dark';
}
/**
 * Add backdrop and keyboard support to a Tippy instance
 */
export function addTippyBackdropSupport(tippyInstance, backdrop, options) {
    installEscapePriorityListener();
    suppressNativeContextMenu(options?.targetElement || null);
    suppressNativeContextMenu(tippyInstance.popper || null);
    suppressNativeContextMenu(backdrop);
    const originalOnShow = tippyInstance.props.onShow;
    const originalOnShown = tippyInstance.props.onShown;
    const originalOnHide = tippyInstance.props.onHide;
    if (options?.onEscape) {
        tippyEscapeHandlers.set(tippyInstance, options.onEscape);
    }
    // Add backdrop click handler to close menu
    const handleBackdropClick = () => {
        if (tippyInstance.state.isVisible) {
            tippyInstance.hide();
        }
    };
    tippyInstance.setProps({
        onShow: (instance) => {
            backdrop.classList.add('visible');
            if (options?.targetElement && options?.activeClass) {
                options.targetElement.classList.add(options.activeClass);
            }
            removeFromVisibleStack(instance);
            visibleTippyStack.push(instance);
            // Add backdrop click handler
            backdrop.addEventListener('click', handleBackdropClick);
            if (originalOnShow)
                originalOnShow(instance);
        },
        onShown: (instance) => {
            if (originalOnShown)
                originalOnShown(instance);
        },
        onHide: (instance) => {
            backdrop.classList.remove('visible');
            if (options?.targetElement && options?.activeClass) {
                options.targetElement.classList.remove(options.activeClass);
            }
            removeFromVisibleStack(instance);
            // Remove backdrop click handler
            backdrop.removeEventListener('click', handleBackdropClick);
            if (originalOnHide)
                originalOnHide(instance);
        }
    });
}
/**
 * Setup keyboard navigation for menu items
 * Call this after the menu is shown (in onShown callback)
 * @param menu - The menu container element (with .plugin-menu class)
 * @param itemSelector - CSS selector for menu items (default: '.plugin-menu-item')
 * @param autoFocusFirst - Whether to auto-focus the first item (default: false for context menus)
 */
const menuKeyboardNavCleanups = new WeakMap();
export function setupMenuKeyboardNav(menu, itemSelector = '.plugin-menu-item', autoFocusFirst = false) {
    // Replace any prior binding on this node (menus often rebuild on show).
    menuKeyboardNavCleanups.get(menu)?.();
    const items = Array.from(menu.querySelectorAll(itemSelector));
    if (items.length === 0)
        return;
    let focusedIndex = -1; // -1 means no item focused initially
    const updateFocus = () => {
        items.forEach((el, i) => {
            el.classList.toggle('focused', i === focusedIndex);
        });
    };
    const handleKeydown = (e) => {
        const keyEvent = e;
        if (keyEvent.key === 'ArrowDown') {
            e.preventDefault();
            focusedIndex =
                focusedIndex < 0 ? 0 : (focusedIndex + 1) % items.length;
            updateFocus();
        }
        else if (keyEvent.key === 'ArrowUp') {
            e.preventDefault();
            focusedIndex =
                focusedIndex < 0
                    ? items.length - 1
                    : (focusedIndex - 1 + items.length) % items.length;
            updateFocus();
        }
        else if (keyEvent.key === 'Enter' && focusedIndex >= 0) {
            e.preventDefault();
            items[focusedIndex].click();
        }
    };
    menu.addEventListener('keydown', handleKeydown);
    menuKeyboardNavCleanups.set(menu, () => {
        menu.removeEventListener('keydown', handleKeydown);
        menuKeyboardNavCleanups.delete(menu);
    });
    if (autoFocusFirst) {
        focusedIndex = 0;
        updateFocus();
    }
    menu.focus();
}
